import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react'
import { useChatStore } from '../../../stores/chatStore'

interface Props {
    children: ReactNode
    currentAgentId: string | null
    currentView: string
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: any
}

export class ChatPanelErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        }
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: any) {
        // Capturar contexto completo
        const context = {
            view: this.props.currentView,
            agentId: this.props.currentAgentId,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            url: window.location.href
        }

        // Enviar para Sentry com contexto rico
        Sentry.captureException(error, {
            contexts: {
                errorInfo,
                chatState: context
            },
            tags: {
                component: 'ChatPanelMaster',
                severity: 'critical',
                view: context.view
            },
            level: 'error'
        })

        this.setState({ errorInfo })
    }

    handleReset = () => {
        const { currentAgentId } = this.props

        // Limpar estado corrompido no Zustand
        if (currentAgentId) {
            const store = useChatStore.getState()
            // Reset apenas do agente atual, não de tudo
            store.setMessages(currentAgentId as string, [], '')
            store.setStreamingMessage(currentAgentId as string, null)
            store.setMessageLoadError(currentAgentId as string, null)
        }

        // Reset do ErrorBoundary
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        })
    }

    handleReload = () => {
        window.location.reload()
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-black">
                    <div className="text-center max-w-md">
                        <div className="text-red-500 mb-4">
                            <AlertTriangle className="w-16 h-16 mx-auto" />
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Chat Temporariamente Indisponível
                        </h2>

                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            Ocorreu um erro inesperado. Você pode tentar recuperar ou recarregar a página.
                        </p>

                        {/* Detalhes técnicos (colapsável) */}
                        <details className="text-left mb-6 p-4 bg-gray-100 dark:bg-gray-900 rounded">
                            <summary className="cursor-pointer text-sm font-medium mb-2">
                                Detalhes Técnicos
                            </summary>
                            <pre className="text-xs overflow-auto">
                                {this.state.error?.message}
                            </pre>
                        </details>

                        {/* Ações */}
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={this.handleReset}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Tentar Novamente
                            </button>

                            <button
                                onClick={this.handleReload}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
                            >
                                Recarregar Página
                            </button>

                            <a
                                href="https://github.com/HIABrasil/orion/issues/new"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Reportar Problema
                            </a>
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}
